import { AxiosResponse } from 'axios'
import { queryOptions } from '@tanstack/react-query'
import axiosAuth from './config'
import { createUrl } from './helpers'
import { NSocietyGuides } from '../interfaces/services/society-guides.interfaces'
import { ISocietyGuideId } from '../interfaces/models/society-guides.interfaces'

const GUIDE_ID = {
  GETTING_STARTED: 'gettingStarted',
  GET_TO_KNOW: 'getToKnow',
} as const

const STEPS = {
  GET_TO_KNOW: {
    BOARD: 'board',
    FEED: 'feed',
    CHAT: 'chat',
    CALENDAR: 'calendar',
  },
  GETTING_STARTED: {
    BILLING: 'billing',
    SOCIETY_IMAGE: 'society_image',
    REGISTER: 'register',
    FUNCTIONS: 'functions',
    MEMBERS: 'members',
  },
} as const

export type TSocietyGuideStepGetToKnow =
  (typeof STEPS.GET_TO_KNOW)[keyof typeof STEPS.GET_TO_KNOW]
export type TSocietyGuideStepGettingStarted =
  (typeof STEPS.GETTING_STARTED)[keyof typeof STEPS.GETTING_STARTED]
export type TSocietyGuideId = (typeof GUIDE_ID)[keyof typeof GUIDE_ID]

export const CONSTANTS_SOCIETY_GUIDES = {
  GUIDE_ID,
  STEPS,
} as const

const getSocietyGuide = (
  societyId: string,
  guideId: ISocietyGuideId
): Promise<AxiosResponse<NSocietyGuides.NGet.IResponse>> => {
  return axiosAuth.get<NSocietyGuides.NGet.IResponse>(
    createUrl(`/society-guides/${guideId}/${societyId}`)
  )
}

export const setGuideStep = ({
  societyId,
  id,
  completed,
  guideId,
}: {
  societyId: string
  id: string
  completed: boolean
  guideId: ISocietyGuideId
}): Promise<AxiosResponse<NSocietyGuides.NSetStep.IResponse>> => {
  return axiosAuth.post<NSocietyGuides.NSetStep.IResponse>(
    createUrl(`/society-guides/${guideId}/${societyId}/step`),
    {
      id,
      completed,
    }
  )
}

export const completeGuide = ({
  societyId,
  completed,
  guideId,
}: {
  societyId: string
  completed: boolean
  guideId: ISocietyGuideId
}): Promise<AxiosResponse<NSocietyGuides.NSetCompleted.IResponse>> => {
  return axiosAuth.post<NSocietyGuides.NSetCompleted.IResponse>(
    createUrl(`/society-guides/${guideId}/${societyId}/complete`),
    {
      completed,
    }
  )
}

export const startGuide = ({
  societyId,
  completed,
  guideId,
}: {
  societyId: string
  completed: boolean
  guideId: ISocietyGuideId
}): Promise<AxiosResponse<NSocietyGuides.NSetStart.IResponse>> => {
  return axiosAuth.post<NSocietyGuides.NSetStart.IResponse>(
    createUrl(`/society-guides/${guideId}/${societyId}/start`),
    {
      completed,
    }
  )
}

export const societyGuidesQueries = {
  getToKnow: (societyId?: string, isAdmin?: boolean) =>
    queryOptions({
      queryKey: [
        { scope: 'societyGuides', entity: 'getToKnow', societyId },
      ] as const,
      queryFn: async ({ queryKey }) => {
        const response = await getSocietyGuide(
          queryKey[0].societyId!,
          GUIDE_ID.GET_TO_KNOW
        )
        return response.data.data
      },
      enabled: !!societyId && !!isAdmin,
    }),
  gettingStarted: (societyId?: string, isAdmin?: boolean) =>
    queryOptions({
      queryKey: [
        { scope: 'societyGuides', entity: 'getGettingStarted', societyId },
      ] as const,
      queryFn: async ({ queryKey }) => {
        const response = await getSocietyGuide(
          queryKey[0].societyId!,
          GUIDE_ID.GETTING_STARTED
        )
        return response.data.data
      },
      enabled: !!societyId && !!isAdmin,
    }),
}
